import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von03RespSupport } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface Von03RespSupportFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von03RespSupportForm: React.FC<Von03RespSupportFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  // Surfactant fields
  const [drSurfactant, setDrSurfactant] = useState(false);
  const [rdsSurfGiven, setRdsSurfGiven] = useState(false);
  const [rdsSurfDose1Mgkg, setRdsSurfDose1Mgkg] = useState<string>('');
  const [rdsFirstSurfAgeH, setRdsFirstSurfAgeH] = useState<string>('');
  const [rdsFirstSurfAgeMin, setRdsFirstSurfAgeMin] = useState<string>('');

  // Respiratory support after initial resuscitation
  const [oxAfterInitialResus, setOxAfterInitialResus] = useState(false);
  const [ventAfterInitialResus, setVentAfterInitialResus] = useState(false);
  const [hfvAfterInitialResus, setHfvAfterInitialResus] = useState(false);
  const [ncf, setNcf] = useState(false);
  const [ncfAfterInitialResus, setNcfAfterInitialResus] = useState(false);
  const [nimvAfterInitialResus, setNimvAfterInitialResus] = useState(false);
  const [cpapAfterInitialResus, setCpapAfterInitialResus] = useState(false);

  // Inhaled nitric oxide
  const [ino, setIno] = useState(false);
  const [inoGiven, setInoGiven] = useState<string>('');

  // Conditions
  const [rds, setRds] = useState(false);
  const [ptx, setPtx] = useState(false);
  const [ptxWhere, setPtxWhere] = useState<string>('');
  const [chronicLungDisease, setChronicLungDisease] = useState(false);
  const [respOther, setRespOther] = useState<string>('');
  const [oxDay28, setOxDay28] = useState(false);

  // Additional treatments
  const [caffeineAny, setCaffeineAny] = useState(false);
  const [vitaIm, setVitaIm] = useState(false);

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getRespSupport(patientId);

      if (data) {
        setHasExistingData(true);
        setDrSurfactant(Boolean(data.dr_surfactant));
        setRdsSurfGiven(Boolean(data.rds_surf_given));
        setRdsSurfDose1Mgkg(data.rds_surf_dose1_mgkg?.toString() || '');
        setRdsFirstSurfAgeH(data.rds_first_surf_age_h?.toString() || '');
        setRdsFirstSurfAgeMin(data.rds_first_surf_age_min?.toString() || '');
        setOxAfterInitialResus(Boolean(data.ox_after_initial_resus));
        setVentAfterInitialResus(Boolean(data.vent_after_initial_resus));
        setHfvAfterInitialResus(Boolean(data.hfv_after_initial_resus));
        setNcf(Boolean(data.ncf_));
        setNcfAfterInitialResus(Boolean(data.ncf_after_initial_resus));
        setNimvAfterInitialResus(Boolean(data.nimv_after_initial_resus));
        setCpapAfterInitialResus(Boolean(data.cpap_after_initial_resus));
        setIno(Boolean(data.ino));
        setInoGiven(data.ino_given?.toString() || '');
        setRds(Boolean(data.rds));
        setPtx(Boolean(data.ptx));
        setPtxWhere(data.ptx_where?.toString() || '');
        setChronicLungDisease(Boolean(data.chronic_lung_disease));
        setRespOther(data.resp_other || '');
        setOxDay28(Boolean(data.ox_day28));
        setCaffeineAny(Boolean(data.caffeine_any));
        setVitaIm(Boolean(data.vita_im));
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von03RespSupport> = {
        patient_id: patientId,
        dr_surfactant: drSurfactant,
        rds_surf_given: rdsSurfGiven,
        rds_surf_dose1_mgkg: rdsSurfDose1Mgkg ? parseFloat(rdsSurfDose1Mgkg) : undefined,
        rds_first_surf_age_h: rdsFirstSurfAgeH ? parseInt(rdsFirstSurfAgeH) : undefined,
        rds_first_surf_age_min: rdsFirstSurfAgeMin ? parseInt(rdsFirstSurfAgeMin) : undefined,
        ox_after_initial_resus: oxAfterInitialResus,
        vent_after_initial_resus: ventAfterInitialResus,
        hfv_after_initial_resus: hfvAfterInitialResus,
        ncf_: ncf,
        ncf_after_initial_resus: ncfAfterInitialResus,
        nimv_after_initial_resus: nimvAfterInitialResus,
        cpap_after_initial_resus: cpapAfterInitialResus,
        ino: ino,
        ino_given: inoGiven ? parseInt(inoGiven) : undefined,
        rds: rds,
        ptx: ptx,
        ptx_where: ptxWhere ? parseInt(ptxWhere) : undefined,
        chronic_lung_disease: chronicLungDisease,
        resp_other: respOther,
        ox_day28: oxDay28,
        caffeine_any: caffeineAny,
        vita_im: vitaIm
      };

      if (hasExistingData) {
        await vonDataService.updateRespSupport(patientId, formData);
      } else {
        await vonDataService.createRespSupport(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 03: Suport respirator</CardTitle>
        <CardDescription>Surfactant și suport respirator</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Surfactant */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Surfactant</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="drSurfactant"
                  checked={drSurfactant}
                  onCheckedChange={(checked) => setDrSurfactant(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="drSurfactant" className="text-sm font-normal cursor-pointer">
                  Surfactant la resuscitare inițială
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rdsSurfGiven"
                  checked={rdsSurfGiven}
                  onCheckedChange={(checked) => setRdsSurfGiven(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="rdsSurfGiven" className="text-sm font-normal cursor-pointer">
                  Surfactant administrat (oricând)
                </Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="surfDose">Prima doză surfactant (mg/kg)</Label>
                <Input
                  id="surfDose"
                  type="number"
                  step="0.01"
                  min="0"
                  max="400"
                  value={rdsSurfDose1Mgkg}
                  onChange={(e) => setRdsSurfDose1Mgkg(e.target.value)}
                  placeholder="0-400"
                  disabled={readOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="surfAgeH">Vârsta la prima doză (ore)</Label>
                <Input
                  id="surfAgeH"
                  type="number"
                  min="0"
                  max="2000"
                  value={rdsFirstSurfAgeH}
                  onChange={(e) => setRdsFirstSurfAgeH(e.target.value)}
                  placeholder="0-2000"
                  disabled={readOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="surfAgeMin">Vârsta la prima doză (minute)</Label>
                <Input
                  id="surfAgeMin"
                  type="number"
                  min="0"
                  max="59"
                  value={rdsFirstSurfAgeMin}
                  onChange={(e) => setRdsFirstSurfAgeMin(e.target.value)}
                  placeholder="0-59"
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          {/* Respiratory support after initial resuscitation */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Suport respirator</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="oxAfterResus"
                  checked={oxAfterInitialResus}
                  onCheckedChange={(checked) => setOxAfterInitialResus(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="oxAfterResus" className="text-sm font-normal cursor-pointer">
                  Oxigen
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ncf"
                  checked={ncf}
                  onCheckedChange={(checked) => setNcf(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ncf" className="text-sm font-normal cursor-pointer">
                  Canulă nazală cu flux înalt
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ncfAfterResus"
                  checked={ncfAfterInitialResus}
                  onCheckedChange={(checked) => setNcfAfterInitialResus(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ncfAfterResus" className="text-sm font-normal cursor-pointer">
                  Canulă nazală
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="cpapAfterResus"
                  checked={cpapAfterInitialResus}
                  onCheckedChange={(checked) => setCpapAfterInitialResus(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="cpapAfterResus" className="text-sm font-normal cursor-pointer">
                  CPAP nazal
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="nimvAfterResus"
                  checked={nimvAfterInitialResus}
                  onCheckedChange={(checked) => setNimvAfterInitialResus(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="nimvAfterResus" className="text-sm font-normal cursor-pointer">
                  Ventilație (IMV) nazală
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ventAfterResus"
                  checked={ventAfterInitialResus}
                  onCheckedChange={(checked) => setVentAfterInitialResus(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ventAfterResus" className="text-sm font-normal cursor-pointer">
                  Ventilație convențională
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hfvAfterResus"
                  checked={hfvAfterInitialResus}
                  onCheckedChange={(checked) => setHfvAfterInitialResus(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="hfvAfterResus" className="text-sm font-normal cursor-pointer">
                  Ventilație cu frecvență înaltă
                </Label>
              </div>
            </div>
          </div>

          {/* Inhaled nitric oxide */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Oxid nitric inhalat</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ino"
                  checked={ino}
                  onCheckedChange={(checked) => setIno(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ino" className="text-sm font-normal cursor-pointer">
                  Oxid nitric inhalat
                </Label>
              </div>

              {ino && (
                <div className="space-y-2">
                  <Label htmlFor="inoGiven">Unde a fost administrat</Label>
                  <Select value={inoGiven} onValueChange={setInoGiven} disabled={readOnly}>
                    <SelectTrigger id="inoGiven">
                      <SelectValue placeholder="Selectează..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">NICU local</SelectItem>
                      <SelectItem value="2">NICU de transfer</SelectItem>
                      <SelectItem value="3">Ambele</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Conditions */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Afecțiuni respiratorii</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rds"
                  checked={rds}
                  onCheckedChange={(checked) => setRds(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="rds" className="text-sm font-normal cursor-pointer">
                  Sindrom de detresă respiratorie
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ptx"
                  checked={ptx}
                  onCheckedChange={(checked) => setPtx(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ptx" className="text-sm font-normal cursor-pointer">
                  Pneumotorax
                </Label>
              </div>

              {ptx && (
                <div className="space-y-2">
                  <Label htmlFor="ptxWhere">Unde a apărut pneumotoraxul</Label>
                  <Select value={ptxWhere} onValueChange={setPtxWhere} disabled={readOnly}>
                    <SelectTrigger id="ptxWhere">
                      <SelectValue placeholder="Selectează..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">NICU local</SelectItem>
                      <SelectItem value="2">Alt spital</SelectItem>
                      <SelectItem value="3">Ambele</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="chronicLungDisease"
                  checked={chronicLungDisease}
                  onCheckedChange={(checked) => setChronicLungDisease(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="chronicLungDisease" className="text-sm font-normal cursor-pointer">
                  Boală cronică pulmonară
                </Label>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="respOther">Altele</Label>
                <Input
                  id="respOther"
                  value={respOther}
                  onChange={(e) => setRespOther(e.target.value)}
                  disabled={readOnly}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="oxDay28"
                  checked={oxDay28}
                  onCheckedChange={(checked) => setOxDay28(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="oxDay28" className="text-sm font-normal cursor-pointer">
                  Oxigen la ziua 28
                </Label>
              </div>
            </div>
          </div>

          {/* Additional treatments */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Tratamente adiționale</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="caffeineAny"
                  checked={caffeineAny}
                  onCheckedChange={(checked) => setCaffeineAny(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="caffeineAny" className="text-sm font-normal cursor-pointer">
                  Cafeină (orice motiv)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="vitaIm"
                  checked={vitaIm}
                  onCheckedChange={(checked) => setVitaIm(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="vitaIm" className="text-sm font-normal cursor-pointer">
                  Vitamina A intramuscular
                </Label>
              </div>
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
